import { useEffect, useState } from 'react';
import { Building2, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { bearerAuthHeader } from '../utils/apiHeaders';
import { COMPANY_SIZES, INDUSTRIES } from '../utils/companyProfile';

/**
 * Company Settings › Company profile (RESTORE_PLAN R3.3, carrying the old
 * CompanyProfileEditor): name, website, industry, size, description, and the
 * owner (a member of the consulting firm) with who created the company. The
 * firm edits everything; the company's own admin edits all but the owner.
 * `GET /companies/:id/profile`, `PATCH /companies/:id`.
 */

const API = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
const NONE = '__none__';

export interface CompanyProfile {
  name: string;
  website: string;
  industry: string;
  companySize: string;
  description: string;
  ownerId: string | null;
  ownerName: string | null;
  createdByName: string | null;
}

type Editable = Pick<CompanyProfile, 'name' | 'website' | 'industry' | 'companySize' | 'description' | 'ownerId'>;

/** The fields that changed, as the PATCH body (the owner only when the editor may set it). */
export function profileChanges(before: Editable, after: Editable, canEditOwner: boolean): Partial<Editable> {
  const out: Partial<Editable> = {};
  for (const key of ['name', 'website', 'industry', 'companySize', 'description'] as const) {
    if ((after[key] ?? '').trim() !== (before[key] ?? '').trim()) out[key] = (after[key] ?? '').trim();
  }
  if (canEditOwner && (after.ownerId ?? null) !== (before.ownerId ?? null)) out.ownerId = after.ownerId ?? '';
  return out;
}

const editable = (p: CompanyProfile): Editable => ({
  name: p.name, website: p.website, industry: p.industry, companySize: p.companySize, description: p.description, ownerId: p.ownerId,
});

export function CompanyProfileCard({ companyId, onSaved }: { companyId: string; onSaved?: (profile: CompanyProfile) => void }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [form, setForm] = useState<Editable | null>(null);
  const [owners, setOwners] = useState<{ id: string; name: string }[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [canEditOwner, setCanEditOwner] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/companies/${companyId}/profile`, { headers: { ...bearerAuthHeader() } });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Could not load the company profile');
        if (!live) return;
        setProfile(body.profile);
        setForm(editable(body.profile));
        setOwners(body.owners || []);
        setCanEdit(!!body.canEdit);
        setCanEditOwner(!!body.canEditOwner);
      } catch (e: any) {
        if (live) toast.error(e?.message || 'Could not load the company profile');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [companyId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading company profile" />
        </CardContent>
      </Card>
    );
  }
  if (!profile || !form) return null;

  const changes = profileChanges(editable(profile), form, canEditOwner);
  const dirty = Object.keys(changes).length > 0;
  const set = (patch: Partial<Editable>) => setForm({ ...form, ...patch });

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}`, {
        method: 'PATCH',
        headers: { ...bearerAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not save the company profile');
      const next: CompanyProfile = {
        ...profile,
        ...body.profile,
        ownerName: owners.find((o) => o.id === body.profile?.ownerId)?.name ?? profile.ownerName,
        createdByName: profile.createdByName,
      };
      setProfile(next);
      setForm(editable(next));
      toast.success('Company profile saved');
      onSaved?.(next);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the company profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="size-5 text-blue-500" aria-hidden="true" />
              Company profile
            </CardTitle>
            <CardDescription>Who the client is: shown to the consulting team and used in AI context</CardDescription>
          </div>
          {canEdit && dirty && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setForm(editable(profile))} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving}>
                {saving ? <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" /> : <Save className="size-4 mr-2" aria-hidden="true" />}
                Save profile
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Company name</Label>
            <Input id="profile-name" value={form.name} onChange={(e) => set({ name: e.target.value })} disabled={!canEdit} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-website">Website</Label>
            <Input id="profile-website" type="url" value={form.website} placeholder="https://www.example.com" onChange={(e) => set({ website: e.target.value })} disabled={!canEdit} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-industry">Industry</Label>
            <Select value={form.industry || NONE} onValueChange={(v) => set({ industry: v === NONE ? '' : v })} disabled={!canEdit}>
              <SelectTrigger id="profile-industry" aria-label="Industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not set</SelectItem>
                {INDUSTRIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-size">Company size</Label>
            <Select value={form.companySize || NONE} onValueChange={(v) => set({ companySize: v === NONE ? '' : v })} disabled={!canEdit}>
              <SelectTrigger id="profile-size" aria-label="Company size"><SelectValue placeholder="Select company size" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not set</SelectItem>
                {COMPANY_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-description">Description</Label>
          <Textarea id="profile-description" rows={3} value={form.description} placeholder="Brief description of the company..." onChange={(e) => set({ description: e.target.value })} disabled={!canEdit} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
          <div className="space-y-2">
            <Label htmlFor="profile-owner">Owner (BD rep or consultant)</Label>
            {canEditOwner ? (
              <Select value={form.ownerId || NONE} onValueChange={(v) => set({ ownerId: v === NONE ? null : v })}>
                <SelectTrigger id="profile-owner" aria-label="Owner"><SelectValue placeholder="Assign owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No owner</SelectItem>
                  {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <p id="profile-owner">{profile.ownerName || 'Not assigned'}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Created by</Label>
            <p className="text-muted-foreground">{profile.createdByName || 'Unknown'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
